import { ref, computed } from 'vue';
import { validateForm, validateField as validateSingleField } from '@/utils/validation';

/**
 * Form validation composable
 * 
 * @param {Object} options - Configuration options
 * @param {Object} options.schema - The validation schema for form fields
 * @param {Object} options.initialValues - Initial form values (optional)
 * @param {Boolean} options.validateOnChange - If true, validate fields on change (default: true)
 * @param {Boolean} options.validateOnBlur - If true, validate fields on blur (default: true)
 * @returns {Object} - Form validation utilities
 */
export function useFormValidation(options = {}) {
  const {
    schema = {},
    initialValues = {},
    validateOnChange = true,
    validateOnBlur = true
  } = options;
  
  // Form state
  const formData = ref({ ...initialValues });
  const errors = ref({});
  const isValid = ref(true);
  const isDirty = ref(false);
  const touchedFields = ref(new Set());
  
  // Validate a single field
  const validateField = (fieldName) => {
    // Skip validation if field isn't in schema
    if (!schema[fieldName]) {
      return true;
    }
    
    const result = validateSingleField(
      formData.value[fieldName], 
      schema[fieldName].errorMessage, 
      schema[fieldName].validator
    );
    
    // Update errors
    if (result.error) {
      errors.value[fieldName] = result.errorMessage;
    } else {
      delete errors.value[fieldName];
    }
    
    // Mark field as touched
    touchedFields.value.add(fieldName);
    
    // Update isValid
    isValid.value = Object.keys(errors.value).length === 0;
    
    return !result.error;
  };
  
  // Validate all fields
  const validateAll = () => {
    const result = validateForm(formData.value, schema);
    
    // Convert validation results to simple error messages
    errors.value = {};
    
    Object.entries(result.errors).forEach(([field, fieldResult]) => {
      if (fieldResult.error) {
        errors.value[field] = fieldResult.errorMessage;
      }
    });
    
    // Mark all fields as touched
    Object.keys(schema).forEach(field => {
      touchedFields.value.add(field);
    });
    
    // Update isValid
    isValid.value = !result.hasErrors;
    
    return isValid.value;
  };
  
  // Reset the form
  const resetForm = (newValues = {}) => {
    formData.value = { ...newValues };
    errors.value = {};
    isValid.value = true;
    isDirty.value = false;
    touchedFields.value = new Set();
  };
  
  // Set a field value with optional validation
  const setFieldValue = (field, value, validate = validateOnChange) => {
    formData.value[field] = value;
    isDirty.value = true;
    
    if (validate) {
      validateField(field);
    }
  };
  
  // Handle field blur
  const handleBlur = (field) => {
    if (validateOnBlur) {
      validateField(field);
    }
  };
  
  // Get a list of all errors
  const errorMessages = computed(() => Object.values(errors.value));
  
  // Return all the utilities
  return {
    formData,
    errors,
    errorMessages,
    isValid,
    isDirty,
    touchedFields,
    validateField,
    validateAll,
    resetForm,
    setFieldValue,
    handleBlur
  };
} 